// Phase 7 D4 — Track D. XHR-backed upload helper with progress reporting.
//
// `fetch` does not expose upload-side progress events, so multipart uploads
// that need a progress bar (Q-13: file-size + ETA) use XMLHttpRequest. This
// module wraps XHR in a typed promise so the rest of the codebase keeps a
// fetch-shaped surface.
//
// Behaviour:
//   - Resolves with a parsed JSON body on 2xx (any text body is JSON.parse'd;
//     non-JSON 2xx falls through to a typed `invalid_response` rejection).
//   - Rejects on 4xx/5xx with the same `ApiError` / `AuthRequiredError`
//     shape as the fetch-based client, so calling code does not have to
//     branch on transport.
//   - Calls `onProgress` once per `xhr.upload.progress` event with `{ loaded,
//     total, etaSeconds }`. ETA is computed naively from elapsed time and
//     bytes transferred; falls through to `null` when the rate is unknown
//     (very early in the upload or when `lengthComputable` is false).
//   - Network failures (DNS, offline) reject with `NetworkError`.

import { ApiError, AuthRequiredError, NetworkError, decodeError } from './errors.js';

export interface UploadProgress {
  /** Bytes already transferred. */
  readonly loaded: number;
  /** Total bytes to transfer; null when the body is not length-computable. */
  readonly total: number | null;
  /** Estimated seconds remaining; null when not computable yet. */
  readonly etaSeconds: number | null;
}

export interface UploadOptions {
  /** Absolute URL or same-origin path (e.g. '/api/modules/payments/statements'). */
  readonly url: string;
  /** HTTP verb. POST is the only verb supported in v1. */
  readonly method: 'POST' | 'PUT';
  /** FormData body. The caller assembles fields + files. */
  readonly body: FormData;
  /** Optional auth header value (`Bearer <token>`); cookie mode omits this. */
  readonly authHeader?: string;
  /** When true, sets `withCredentials = true` so cookies are sent. */
  readonly withCredentials?: boolean;
  /** Progress callback fired on every `upload.progress` event. */
  readonly onProgress?: (progress: UploadProgress) => void;
  /** Test injection point — defaults to the global `XMLHttpRequest`. */
  readonly xhrFactory?: () => XMLHttpRequest;
}

/**
 * Build a Response-like wrapper from XHR state so we can re-use
 * `decodeError(response)` from the fetch path. Only `status`, `text()`, and
 * `headers.get('content-type')` are read.
 */
function xhrToResponseLike(xhr: XMLHttpRequest): Response {
  const text = xhr.responseText ?? '';
  const headers = new Headers();
  // XHR's getAllResponseHeaders returns a CRLF-delimited string.
  const raw = xhr.getAllResponseHeaders ? xhr.getAllResponseHeaders() : '';
  if (raw) {
    for (const line of raw.split(/\r?\n/)) {
      const idx = line.indexOf(':');
      if (idx <= 0) continue;
      const name = line.slice(0, idx).trim();
      const value = line.slice(idx + 1).trim();
      if (name) headers.set(name, value);
    }
  }
  return new Response(text, { status: xhr.status, headers });
}

/**
 * Multipart upload with optional progress reporting. Returns the parsed
 * 2xx JSON body, or rejects with `ApiError` / `AuthRequiredError` /
 * `NetworkError`.
 */
export function uploadWithProgress<T>(opts: UploadOptions): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const factory =
      opts.xhrFactory ?? (() => new XMLHttpRequest());
    let xhr: XMLHttpRequest;
    try {
      xhr = factory();
    } catch (err) {
      reject(new NetworkError('XMLHttpRequest is not available.', err));
      return;
    }

    const startedAt = Date.now();
    let lastSeen = 0;

    if (opts.onProgress && xhr.upload) {
      xhr.upload.addEventListener('progress', (ev: ProgressEvent) => {
        const elapsed = (Date.now() - startedAt) / 1000;
        const loaded = ev.loaded;
        const total = ev.lengthComputable ? ev.total : null;
        let etaSeconds: number | null = null;
        if (total !== null && elapsed > 0 && loaded > 0) {
          const rate = loaded / elapsed; // bytes/sec
          if (rate > 0) {
            etaSeconds = Math.max(0, (total - loaded) / rate);
          }
        }
        lastSeen = loaded;
        opts.onProgress?.({ loaded, total, etaSeconds });
      });
    }

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        // Mirror the fetch path: any text body parsed as JSON; empty body
        // resolves with `undefined`.
        if (!xhr.responseText) {
          resolve(undefined as T);
          return;
        }
        try {
          resolve(JSON.parse(xhr.responseText) as T);
        } catch {
          reject(
            new ApiError({
              code: 'invalid_response',
              message: 'Server returned a non-JSON 2xx response.',
              status: xhr.status,
            }),
          );
        }
        // Emit a final 100% progress event so the caller's UI completes
        // the bar even if the browser does not fire one (some do not when
        // the response is small).
        if (opts.onProgress && lastSeen >= 0) {
          opts.onProgress({
            loaded: lastSeen || 1,
            total: lastSeen || 1,
            etaSeconds: 0,
          });
        }
        return;
      }
      // Non-2xx — decode using the shared error decoder.
      const responseLike = xhrToResponseLike(xhr);
      decodeError(responseLike).then((err) => reject(err)).catch((err) => reject(err));
    });

    xhr.addEventListener('error', () => {
      reject(new NetworkError('Upload failed before reaching the server.', null));
    });

    xhr.addEventListener('abort', () => {
      reject(new NetworkError('Upload aborted.', null));
    });

    try {
      xhr.open(opts.method, opts.url, true);
      if (opts.withCredentials) {
        xhr.withCredentials = true;
      }
      if (opts.authHeader) {
        xhr.setRequestHeader('Authorization', opts.authHeader);
      }
      // Do NOT set Content-Type — XHR derives the multipart boundary from
      // FormData automatically. Setting it manually breaks the parse.
      xhr.send(opts.body);
    } catch (err) {
      reject(new NetworkError('Failed to dispatch upload.', err));
    }
  });
}

// Re-export the error classes so callers can match without a separate import
// (mirrors the convenience the fetch client already offers).
export { ApiError, AuthRequiredError, NetworkError };
